import { useId } from "react";
import type { ReactNode } from "react";

// DateTimeField (U6) — a humane calendar/time input over the platform's ISO-8601 contract.
// The advisor/admin picks from a native calendar (or one tap of Bugün/Şimdi); the value is
// emitted as ISO-8601 WITH the Europe/Istanbul offset — so the server contract is unchanged,
// only the UI is humanized. Copy (label/today/now/hint/error) is supplied by the consumer's
// i18n, per the DS1 convention. Blank value → "" (the consumer omits it → server now()).

// Europe/Istanbul is UTC+3 year-round (Turkey dropped DST in 2016), so the offset is fixed.
const ISTANBUL_OFFSET = "+03:00";

/** A datetime-local wall-clock (YYYY-MM-DDTHH:mm[:ss]) → ISO-8601 with the Istanbul offset. */
function localToIso(local: string): string {
  if (!local) return "";
  const withSeconds = local.length === 16 ? `${local}:00` : local;
  return `${withSeconds}${ISTANBUL_OFFSET}`;
}

/** ISO-8601 → the datetime-local value the input shows (the wall-clock portion). */
function isoToLocal(iso: string): string {
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/.exec(iso || "");
  return m ? m[1]! : "";
}

/** The current Istanbul wall-clock as ISO-8601+03:00 — correct regardless of the browser's
 *  own timezone (formatted via Intl in the Europe/Istanbul zone). */
function istanbulNow(atMidnight: boolean): string {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(new Date())
    .reduce<Record<string, string>>((a, part) => {
      a[part.type] = part.value;
      return a;
    }, {});
  const hour = p.hour === "24" ? "00" : p.hour;
  const date = `${p.year}-${p.month}-${p.day}`;
  return atMidnight
    ? `${date}T00:00:00${ISTANBUL_OFFSET}`
    : `${date}T${hour}:${p.minute}:${p.second}${ISTANBUL_OFFSET}`;
}

export interface DateTimeFieldProps {
  label: ReactNode;
  /** ISO-8601 with offset, or "" (blank → the consumer omits it → server now()). */
  value: string;
  onChange: (iso: string) => void;
  /** "Bugün" — today at 00:00 Istanbul. */
  todayLabel: ReactNode;
  /** "Şimdi" — the current instant. */
  nowLabel: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  id?: string;
  "data-testid"?: string;
}

export function DateTimeField({
  label,
  value,
  onChange,
  todayLabel,
  nowLabel,
  hint,
  error,
  required,
  id,
  "data-testid": testId,
}: DateTimeFieldProps) {
  const msgId = useId();
  const message = error ?? hint;
  return (
    <div className="ui-field ui-datetime">
      <label>
        {label}
        <input
          type="datetime-local"
          className="ui-input"
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? msgId : undefined}
          data-testid={testId}
          value={isoToLocal(value)}
          onChange={(e) => onChange(localToIso(e.target.value))}
        />
      </label>
      <div className="ui-datetime-shortcuts">
        <button
          type="button"
          className="ui-btn"
          data-testid={testId ? `${testId}-today` : undefined}
          onClick={() => onChange(istanbulNow(true))}
        >
          {todayLabel}
        </button>
        <button
          type="button"
          className="ui-btn"
          data-testid={testId ? `${testId}-now` : undefined}
          onClick={() => onChange(istanbulNow(false))}
        >
          {nowLabel}
        </button>
      </div>
      {message ? (
        <span id={msgId} className={error ? "field-error" : "muted"}>
          {message}
        </span>
      ) : null}
    </div>
  );
}
